import { Component, createRef } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import socketIOClient from "socket.io-client";
import { setHostingRoomAC, setPlayerConfigAC, switchStateAC } from "../../actions/game";
import { applyThemeVars } from "../../branding/theme";
import {
    addToRoom,
    answerRejectedEliminated,
    answersClose,
    answersOpen,
    gameCompleted,
    joinedToRoom,
    joinRateLimited,
    nicknameIsTaken,
    playerEliminated,
    playerRoomSnapshot,
    quizStarting,
    roomNotFound,
    server,
    sessionTakenOver
} from "../../connection/config";
import {
    clearSession,
    getOrCreatePlayerId,
    getSession,
    saveSession
} from "../../connection/reconnect";
import { onPlayerJoinGame } from "../../utilities";

import { MAX_WEB_SOCKET_MESSAGE_SIZE } from "../../utilities/constants";
import CountdownOverlay from "../../components/CountdownOverlay";
import Toast from "../../components/Toast";
import ConnectionError from "./ConnectionError";
import Final from "./Final";
import LoadingRoom from "./LoadingRoom";
import NicknameIsTaken from "./NicknameIsTaken";
import Question from "./Question";
import RoomNotFound from "./RoomNotFound";
import SessionTakenOver from "./SessionTakenOver";
import { V_COUNTDOWN, V_FINAL, V_LOADING_ROOM, V_NICKNAME_IS_TAKEN, V_QUESTION, V_ROOM_NOT_FOUND, V_WAITING, V_CONNECTION_ERROR, V_SESSION_TAKEN_OVER } from "./views";
import Waiting from "./Waiting";

// Connection logging helper
const logPlayer = (event, ...args) => {
    console.log(`[Player] ${event}`, ...args);
};

// Retry configuration
const JOIN_TIMEOUT_MS = 5000;  // 5 seconds
const MAX_RETRIES = 3;

class Player extends Component {

    constructor(props) {
        super(props);

        // Determine session data: from props (normal join) or localStorage (refresh/rejoin)
        const sessionData = this.resolveSessionData();

        this.state = {
            // Session info (from props or localStorage)
            sessionData,  // { roomCode, playerName, playerId } or null
            shouldRedirectHome: sessionData === null,

            // Quiz state
            question: null,
            selectedAnswer: null,
            correctAnswer: null,
            answersOpen: false,
            timerValue: 0,
            questionStart: null,  // Server timestamp when question started (for local countdown)
            timeLimit: 30,        // Time limit for current question
            autoAdvanceAt: null,  // Timestamp for next question countdown
            errorMessage: null,
            startsAt: null,  // Timestamp when quiz countdown will complete

            // Knockout mode state
            knockoutEnabled: false,           // Whether knockout mode is active
            playerStatus: "active",           // "active" or "out"
            eliminatedAtQuestionIndex: null,  // Question index where player was eliminated

            // Connection state tracking
            connectionStatus: "connecting",
            retryCount: 0,
            joinConfirmed: false
        };

        this.autoAdvanceInterval = null;
        this.answerCountdownInterval = null;  // Local timer for answer countdown

        this.selected = this.selected.bind(this);
        this.emitJoinRoom = this.emitJoinRoom.bind(this);
        this.startJoinTimeout = this.startJoinTimeout.bind(this);
        this.clearJoinTimeout = this.clearJoinTimeout.bind(this);
        this.handleJoinTimeout = this.handleJoinTimeout.bind(this);
        this.initializeSocket = this.initializeSocket.bind(this);

        this.joinTimeoutId = null;
        this.toastRef = createRef();
        this.socket = null;
    }

    /**
     * Resolve session data from props or localStorage.
     * Priority: props (user just joined) > localStorage (page refresh/rejoin)
     */
    resolveSessionData() {
        const { game } = this.props;

        // Check props first (user navigated from join screen)
        if (game.roomCode && game.playerName) {
            logPlayer('Session from props', {
                roomCode: game.roomCode,
                playerName: game.playerName,
                hasAvatar: !!game.avatarThumb
            });
            return {
                roomCode: game.roomCode,
                playerName: game.playerName,
                playerId: getOrCreatePlayerId(),
                avatarThumb: game.avatarThumb || null
            };
        }

        // Check localStorage (page refresh scenario)
        const savedSession = getSession();
        if (savedSession) {
            logPlayer('Session from localStorage', {
                roomCode: savedSession.roomCode,
                nickname: savedSession.nickname
            });
            return {
                roomCode: savedSession.roomCode,
                playerName: savedSession.nickname,
                playerId: savedSession.playerId
            };
        }

        // No session available
        logPlayer('No session found in props or localStorage');
        return null;
    }

    /**
     * Emit ADD_TO_ROOM event with playerId for bulletproof reconnection.
     */
    emitJoinRoom() {
        const { sessionData } = this.state;
        if (!sessionData) return;

        const payload = {
            roomCode: sessionData.roomCode,
            nickname: sessionData.playerName,
            playerId: sessionData.playerId,
            avatarThumb: sessionData.avatarThumb || null
        };

        logPlayer('Emitting ADD_TO_ROOM', {
            roomCode: payload.roomCode,
            nickname: payload.nickname,
            playerId: payload.playerId,
            hasAvatar: !!payload.avatarThumb,
            attempt: this.state.retryCount + 1,
            maxRetries: MAX_RETRIES
        });
        this.socket.emit(addToRoom, payload);

        // Start timeout for join confirmation
        this.startJoinTimeout();
    }

    /**
     * Start a timeout waiting for join confirmation.
     */
    startJoinTimeout() {
        this.clearJoinTimeout();
        this.setState({ connectionStatus: "waiting" });

        this.joinTimeoutId = setTimeout(() => {
            this.handleJoinTimeout();
        }, JOIN_TIMEOUT_MS);
    }

    /**
     * Clear any pending join timeout.
     */
    clearJoinTimeout() {
        if (this.joinTimeoutId) {
            clearTimeout(this.joinTimeoutId);
            this.joinTimeoutId = null;
        }
    }

    /**
     * Handle join timeout - retry or show error.
     */
    handleJoinTimeout() {
        const { retryCount, joinConfirmed } = this.state;

        // If already joined, ignore
        if (joinConfirmed) return;

        if (retryCount < MAX_RETRIES - 1) {
            // Retry
            const newRetryCount = retryCount + 1;
            logPlayer('Join timeout, retrying', {
                attempt: newRetryCount + 1,
                maxRetries: MAX_RETRIES
            });

            this.setState({
                retryCount: newRetryCount,
                connectionStatus: "retrying"
            });

            // Small delay before retry
            setTimeout(() => {
                if (this.socket && this.socket.connected && !this.state.joinConfirmed) {
                    this.emitJoinRoom();
                }
            }, 500);
        } else {
            // Exhausted retries
            logPlayer('Join failed after max retries', { maxRetries: MAX_RETRIES });
            this.setState({
                errorMessage: "Could not connect to the room. The server may be busy or the room may no longer exist.",
                connectionStatus: "failed"
            });
            this.props.switchState(V_CONNECTION_ERROR);
            clearSession();
        }
    }

    /**
     * Mark join as confirmed and clear timeout.
     */
    confirmJoin() {
        this.clearJoinTimeout();
        this.setState({ joinConfirmed: true });
    }

    /**
     * Clear auto-advance countdown interval.
     */
    clearAutoAdvanceInterval() {
        if (this.autoAdvanceInterval) {
            clearInterval(this.autoAdvanceInterval);
            this.autoAdvanceInterval = null;
        }
    }

    /**
     * Start auto-advance countdown interval.
     * Updates timerValue based on remaining time until autoAdvanceAt.
     */
    startAutoAdvanceCountdown(autoAdvanceAt) {
        this.clearAutoAdvanceInterval();

        const updateTimer = () => {
            const remaining = Math.max(0, Math.ceil((autoAdvanceAt - Date.now()) / 1000));
            this.setState({ timerValue: remaining });

            if (remaining <= 0) {
                this.clearAutoAdvanceInterval();
            }
        };

        // Initial update
        updateTimer();
        // Update every 100ms for smooth countdown
        this.autoAdvanceInterval = setInterval(updateTimer, 100);
    }

    /**
     * Clear answer countdown interval.
     */
    clearAnswerCountdownInterval() {
        if (this.answerCountdownInterval) {
            clearInterval(this.answerCountdownInterval);
            this.answerCountdownInterval = null;
        }
    }

    /**
     * Start local answer countdown interval.
     * Computes remaining time locally from questionStart + timeLimit.
     * This eliminates the need for TIMER_SYNC broadcasts from the server.
     */
    startAnswerCountdown(questionStart, timeLimit) {
        this.clearAnswerCountdownInterval();
        this.clearAutoAdvanceInterval();  // Stop any auto-advance countdown

        const updateTimer = () => {
            const elapsed = Math.floor((Date.now() - questionStart) / 1000);
            const remaining = Math.max(0, timeLimit - elapsed);
            this.setState({ timerValue: remaining });

            // Stop interval when time runs out (but keep showing 0)
            if (remaining <= 0) {
                this.clearAnswerCountdownInterval();
            }
        };

        // Initial update
        updateTimer();
        // Update every 200ms (smooth enough for display, not too CPU-heavy)
        this.answerCountdownInterval = setInterval(updateTimer, 200);
    }

    /**
     * Initialize socket connection and set up event handlers.
     */
    initializeSocket() {
        const { sessionData } = this.state;

        logPlayer('Initializing socket connection', {
            server,
            roomCode: sessionData.roomCode,
            playerName: sessionData.playerName
        });

        this.socket = socketIOClient(server, {
            closeOnBeforeunload: false,
            maxHttpBufferSize: MAX_WEB_SOCKET_MESSAGE_SIZE
        });

        // Connection lifecycle events
        this.socket.on('connect', () => {
            logPlayer('Socket connected', { socketId: this.socket.id });
            this.setState({ connectionStatus: "connected" });
            this.emitJoinRoom();
        });

        this.socket.on('disconnect', (reason) => {
            logPlayer('Socket disconnected', { reason });
            this.clearJoinTimeout();

            // Socket.IO will attempt to reconnect automatically for most reasons
            // Only show error for permanent disconnections
            if (reason === 'io server disconnect' || reason === 'io client disconnect') {
                this.setState({ errorMessage: `Disconnected: ${reason}` });
                this.props.switchState(V_CONNECTION_ERROR);
            }
        });

        this.socket.on('connect_error', (error) => {
            logPlayer('Socket connection error', { error: error.message });
            this.clearJoinTimeout();
            this.setState({ errorMessage: `Connection failed: ${error.message}` });
            this.props.switchState(V_CONNECTION_ERROR);
        });

        // Server response events
        this.socket.on(nicknameIsTaken, () => {
            logPlayer('Received NICKNAME_IS_TAKEN');
            this.confirmJoin();
            this.props.switchState(V_NICKNAME_IS_TAKEN);
            clearSession();
        });

        this.socket.on(roomNotFound, () => {
            logPlayer('Received ROOM_NOT_FOUND - room closed or does not exist');
            this.confirmJoin();
            this.setState({
                errorMessage: "The quiz has ended or the room no longer exists."
            });
            this.props.switchState(V_ROOM_NOT_FOUND);
            clearSession();
        });

        this.socket.on(joinRateLimited, ({ reason, retryAfter }) => {
            logPlayer('Received JOIN_RATE_LIMITED', { reason, retryAfter });
            this.confirmJoin();  // Stop retry attempts
            this.setState({
                errorMessage: `${reason} Try again in ${retryAfter} seconds.`
            });
            this.props.switchState(V_CONNECTION_ERROR);
            // Don't clear session - user can try again after waiting
        });

        this.socket.on(joinedToRoom, roomObject => {
            logPlayer('Received JOINED_TO_ROOM', {
                title: roomObject.title,
                isRejoin: roomObject.isRejoin,
                knockoutEnabled: roomObject.knockoutEnabled
            });
            this.confirmJoin();

            const { sessionData } = this.state;

            // Apply host's theme if provided (branded experience for players)
            if (roomObject.theme) {
                try {
                    localStorage.setItem("expo_theme", JSON.stringify(roomObject.theme));
                    applyThemeVars(roomObject.theme);
                } catch (e) {
                    console.warn("Could not apply room theme:", e);
                }
            }

            // Track knockout mode state
            this.setState({
                knockoutEnabled: roomObject.knockoutEnabled ?? false
            });

            onPlayerJoinGame(roomObject.title);
            this.props.setHostingRoom(roomObject);

            // Also update Redux state so other parts of the app have access
            this.props.setPlayerConfig(sessionData.roomCode, sessionData.playerName, true);

            this.props.switchState(V_WAITING);

            // Save/update session in localStorage
            saveSession(sessionData.roomCode, sessionData.playerName, sessionData.playerId);

            // Show toast for successful reconnect
            if (roomObject.isRejoin && this.toastRef.current) {
                this.toastRef.current.show("Reconnected successfully", "success", 2500);
            }
        });

        // Room snapshot: rehydrate into current question state immediately after join
        this.socket.on(playerRoomSnapshot, snapshot => {
            logPlayer('Received PLAYER_ROOM_SNAPSHOT', {
                questionIndex: snapshot.questionIndex,
                hasQuestion: !!snapshot.currentQuestion,
                answersOpen: snapshot.answersOpen,
                playerSelectedAnswer: snapshot.playerSelectedAnswer,
                quizStartsAt: snapshot.quizStartsAt,
                autoAdvanceAt: snapshot.autoAdvanceAt,
                knockoutEnabled: snapshot.knockoutEnabled,
                playerStatus: snapshot.playerStatus,
                eliminatedAtQuestionIndex: snapshot.eliminatedAtQuestionIndex
            });

            // Restore knockout state from snapshot
            this.setState({
                knockoutEnabled: snapshot.knockoutEnabled ?? false,
                playerStatus: snapshot.playerStatus || "active",
                eliminatedAtQuestionIndex: snapshot.eliminatedAtQuestionIndex ?? null
            });

            // If there's a countdown in progress (quiz hasn't started yet), show countdown
            if (snapshot.quizStartsAt && snapshot.quizStartsAt > Date.now()) {
                logPlayer('Rehydrating into countdown', { quizStartsAt: snapshot.quizStartsAt });
                this.setState({ startsAt: snapshot.quizStartsAt });
                this.props.switchState(V_COUNTDOWN);
                return;
            }

            // If there's an active question, rehydrate into it
            if (snapshot.currentQuestion) {
                const timeLimit = snapshot.timeLimit || 30;
                // Calculate remaining timer based on phase
                let timerValue = timeLimit;

                if (snapshot.answersOpen && snapshot.questionStart) {
                    // Answering phase: calculate remaining time from question start
                    const elapsed = Math.floor((Date.now() - snapshot.questionStart) / 1000);
                    timerValue = Math.max(0, timeLimit - elapsed);
                } else if (!snapshot.answersOpen && snapshot.autoAdvanceAt) {
                    // Waiting phase with auto-advance: calculate remaining time until next question
                    timerValue = Math.max(0, Math.ceil((snapshot.autoAdvanceAt - Date.now()) / 1000));
                } else if (!snapshot.answersOpen) {
                    // Waiting phase without auto-advance: show 0
                    timerValue = 0;
                }

                this.setState({
                    question: snapshot.currentQuestion,
                    selectedAnswer: snapshot.playerSelectedAnswer,
                    correctAnswer: null,  // Don't know correct answer yet
                    answersOpen: snapshot.answersOpen,
                    questionStart: snapshot.questionStart || null,
                    timeLimit: timeLimit,
                    autoAdvanceAt: snapshot.autoAdvanceAt || null,
                    timerValue
                });

                // Start appropriate countdown interval based on phase
                if (snapshot.answersOpen && snapshot.questionStart && timerValue > 0) {
                    // Answering phase: start local answer countdown
                    this.startAnswerCountdown(snapshot.questionStart, timeLimit);
                } else if (!snapshot.answersOpen && snapshot.autoAdvanceAt && snapshot.autoAdvanceAt > Date.now()) {
                    // Waiting phase with auto-advance: start auto-advance countdown
                    this.startAutoAdvanceCountdown(snapshot.autoAdvanceAt);
                }

                // Switch to question view
                this.props.switchState(V_QUESTION);
                logPlayer('Rehydrated into active question', {
                    questionIndex: snapshot.questionIndex,
                    timerValue,
                    answersOpen: snapshot.answersOpen,
                    autoAdvanceAt: snapshot.autoAdvanceAt,
                    restoredAnswer: snapshot.playerSelectedAnswer
                });
            }
            // If no active question, player stays on V_WAITING (set by joinedToRoom handler)
        });

        // Quiz countdown starting
        this.socket.on(quizStarting, ({ startsAt }) => {
            logPlayer('Received QUIZ_STARTING', { startsAt });
            this.setState({ startsAt });
            this.props.switchState(V_COUNTDOWN);
        });

        this.socket.on(answersOpen, question => {
            // question now includes questionStart and timeLimit for local countdown
            const questionStart = question.questionStart || Date.now();
            const timeLimit = question.timeLimit || this.props.game.hostingRoom?.timeLimit || 30;

            logPlayer('Received ANSWERS_OPEN', {
                questionIndex: question.index,
                questionStart,
                timeLimit
            });

            this.setState({
                question: question,
                selectedAnswer: null,
                correctAnswer: null,
                answersOpen: true,
                questionStart: questionStart,
                timeLimit: timeLimit,
                timerValue: timeLimit,  // Initial value, will be updated by local countdown
                autoAdvanceAt: null,
                startsAt: null  // Clear countdown state
            });

            // Start local countdown (replaces TIMER_SYNC broadcasts)
            if (timeLimit > 0) {
                this.startAnswerCountdown(questionStart, timeLimit);
            }

            this.props.switchState(V_QUESTION);
        });

        this.socket.on(answersClose, data => {
            const { autoAdvanceAt, ...question } = data;
            logPlayer('Received ANSWERS_CLOSE', { questionIndex: question.index, autoAdvanceAt });

            // Stop the answer countdown (answering phase is over)
            this.clearAnswerCountdownInterval();

            // Stay on question screen, just lock answers and show correct
            this.setState({
                question: question,
                correctAnswer: question.correct,
                answersOpen: false,
                autoAdvanceAt: autoAdvanceAt || null
            });
            // Start auto-advance countdown if applicable
            if (autoAdvanceAt) {
                this.startAutoAdvanceCountdown(autoAdvanceAt);
            }
            // Don't switch to V_WAITING - stay on V_QUESTION
        });

        // Knockout mode: player eliminated after wrong/no answer
        this.socket.on(playerEliminated, ({ questionIndex, reason }) => {
            logPlayer('Received PLAYER_ELIMINATED', { questionIndex, reason });
            this.setState({
                playerStatus: "out",
                eliminatedAtQuestionIndex: questionIndex
            });
            // Show toast notification
            if (this.toastRef.current) {
                const message = reason === "no_answer"
                    ? "You didn't answer in time - you're out!"
                    : "Wrong answer - you're out!";
                this.toastRef.current.show(message, "warning", 4000);
            }
        });

        // Knockout mode: answer rejected because player is eliminated
        this.socket.on(answerRejectedEliminated, ({ reason }) => {
            logPlayer('Received ANSWER_REJECTED_ELIMINATED', { reason });
            // Player is already out, just show toast as reminder
            if (this.toastRef.current) {
                this.toastRef.current.show("You're eliminated and cannot answer", "warning", 2500);
            }
        });

        // TIMER_SYNC no longer needed - timer computed locally from questionStart

        this.socket.on(gameCompleted, stats => {
            logPlayer('Received GAME_COMPLETED');
            this.setState({ stats: stats });
            this.props.switchState(V_FINAL);
            clearSession();
        });

        // Session taken over by another tab/device
        this.socket.on(sessionTakenOver, (data) => {
            logPlayer('Received SESSION_TAKEN_OVER', data);
            this.clearJoinTimeout();
            this.props.switchState(V_SESSION_TAKEN_OVER);
            // Don't clear session - the other tab has taken over
            // Disconnect this socket since we're no longer active
            this.socket.disconnect();
        });

        // Handle race condition: if socket is already connected when listeners are added
        // (can happen with fast localhost connections), manually trigger join
        if (this.socket.connected) {
            logPlayer('Socket already connected on mount, emitting join immediately');
            this.setState({ connectionStatus: "connected" });
            this.emitJoinRoom();
        }
    }

    componentDidMount() {
        const { sessionData, shouldRedirectHome } = this.state;

        logPlayer('Component mounted', {
            hasPropsSession: !!(this.props.game.roomCode && this.props.game.playerName),
            hasLocalStorageSession: !!getSession(),
            resolvedSession: sessionData ? { roomCode: sessionData.roomCode, playerName: sessionData.playerName } : null,
            shouldRedirectHome
        });

        if (shouldRedirectHome) {
            // No session available - redirect to home
            // We set state in constructor; this will trigger redirect in componentDidUpdate
            logPlayer('No session available, will redirect to home');
            return;
        }

        // We have a valid session - proceed with connection
        this.props.switchState(V_LOADING_ROOM);
        this.initializeSocket();
    }

    componentDidUpdate(prevProps, prevState) {
        // Handle redirect to home (done here to avoid React warning about navigate during render)
        if (this.state.shouldRedirectHome && !prevState.shouldRedirectHome) {
            logPlayer('Redirecting to home (no session)');
            this.props.navigate('/');
        }
    }

    componentWillUnmount() {
        this.clearJoinTimeout();
        this.clearAutoAdvanceInterval();
        this.clearAnswerCountdownInterval();
        if (this.socket) {
            this.socket.disconnect();
        }
    }

    selected(selectedAnswer) {
        this.setState({ selectedAnswer });
    };

    render() {
        // Handle redirect state (show nothing while redirecting)
        if (this.state.shouldRedirectHome) {
            return null;
        }

        return (
            <>
                <Toast ref={this.toastRef} />
                {this.renderContent()}
            </>
        );
    }

    renderContent() {
        const { connectionStatus, retryCount, errorMessage } = this.state;

        switch (this.props.game.state) {
            case V_LOADING_ROOM:
                return (
                    <LoadingRoom
                        {...this.props}
                        connectionStatus={connectionStatus}
                        retryCount={retryCount}
                        maxRetries={MAX_RETRIES}
                    />
                );
            case V_NICKNAME_IS_TAKEN:
                return (<NicknameIsTaken {...this.props} />);
            case V_ROOM_NOT_FOUND:
                return (<RoomNotFound {...this.props} errorMessage={errorMessage} />);
            case V_CONNECTION_ERROR:
                return (<ConnectionError {...this.props} errorMessage={errorMessage} />);
            case V_SESSION_TAKEN_OVER:
                return (<SessionTakenOver {...this.props} />);
            case V_WAITING:
                return (
                    <Waiting {...this.props}
                        question={this.state.question}
                        selectedAnswer={this.state.selectedAnswer}
                        correctAnswer={this.state.correctAnswer}
                    />
                );
            case V_COUNTDOWN:
                return (
                    <CountdownOverlay
                        {...this.props}
                        startsAt={this.state.startsAt}
                        cancel="Exit"
                    />
                );
            case V_QUESTION:
                return (
                    <Question {...this.props}
                        socket={this.socket}
                        question={this.state.question}
                        selected={this.selected}
                        selectedAnswer={this.state.selectedAnswer}
                        correctAnswer={this.state.correctAnswer}
                        answersOpen={this.state.answersOpen}
                        timer={this.state.timerValue}
                        knockoutEnabled={this.state.knockoutEnabled}
                        playerStatus={this.state.playerStatus}
                    />
                );
            case V_FINAL:
                return (<Final {...this.props} stats={this.state.stats} />);
            default:
                return (<span>NOT FOUND</span>);
        }
    }
}

const mapStateToProps = state => {
    return {
        game: state.game
    };
};

const mapDispatchToProps = dispatch => ({
    switchState: (...args) => dispatch(switchStateAC(...args)),
    setHostingRoom: (...args) => dispatch(setHostingRoomAC(...args)),
    setPlayerConfig: (...args) => dispatch(setPlayerConfigAC(...args))
});

const ConnectedPlayer = connect(mapStateToProps, mapDispatchToProps)(Player);

const ConnectedPlayerWithNavigate = props => (<ConnectedPlayer {...props} navigate={useNavigate()} />);

export default ConnectedPlayerWithNavigate;
